import { useContext, useMemo } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Settings2 } from 'lucide-react';
import { classesBotao } from '../componentes/base';
import type { DefCampo } from '../componentes/formulario';
import { CtxSessao } from './sessao-contexto';

/**
 * Lote de 19/10 (item 9): campos nativos de uma tela de cadastro que o workspace escondeu ou tornou obrigatórios
 * (Admin > Configurações > Campos das telas; a escolha vem na sessão, `campos_telas`). A chave da tela é a do `module.json`
 * (`cadastros.parceiro`). O servidor recusa o obrigatório vazio; a tela só esconde e marca. Campo escondido não some do
 * registro: o valor que já veio no formulário volta igual no envio.
 */
export interface CamposDaTela {
  /** O campo (pelo nome do formulário ou a chave) aparece. */
  visivel(nome: string): boolean;
  /** O workspace tornou o campo obrigatório. */
  obrigatorio(nome: string): boolean;
  /** As definições com a escolha da tela: as escondidas saem, as obrigatórias ficam marcadas. */
  aplicar(campos: DefCampo[]): DefCampo[];
}

/** A chave configurável a que o nome pertence (`endereco.cep` → `endereco`), entre as gravadas da tela. */
function chaveDoNome(estado: Record<string, unknown>, nome: string): string | null {
  if (nome in estado) return nome;
  const ponto = nome.indexOf('.');
  return ponto > 0 && nome.slice(0, ponto) in estado ? nome.slice(0, ponto) : null;
}

export function useCamposDaTela(tela?: string): CamposDaTela {
  const s = useContext(CtxSessao);
  const estado = (tela && s?.campos_telas?.[tela]) || null;
  return useMemo(() => {
    const ler = (nome: string) => {
      if (!estado) return null;
      const k = chaveDoNome(estado, nome);
      return k ? estado[k]! : null;
    };
    const visivel = (nome: string) => ler(nome)?.visivel !== false;
    const obrigatorio = (nome: string) => !!ler(nome)?.obrigatorio;
    return {
      visivel,
      obrigatorio,
      aplicar: (campos) =>
        estado
          ? campos
              .filter((c) => visivel(c.campoTela ?? c.nome))
              .map((c) => (obrigatorio(c.campoTela ?? c.nome) && !c.obrigatorio ? { ...c, obrigatorio: true } : c))
          : campos,
    };
  }, [estado]);
}

/**
 * Atalho "Configurar campos" no topo do formulário, só para quem configura (`configuracoes.campos_telas`, vendo todas as
 * empresas): abre Admin > Configurações > Campos das telas já nesta tela.
 */
export function AtalhoCamposDaTela({ telaCampos: tela }: { telaCampos: string }) {
  const { t } = useTranslation();
  const s = useContext(CtxSessao);
  if (!s?.pode('configuracoes.campos_telas') || s.empresas?.ve_todas === false || !s.instalado('configuracoes')) return null;
  return (
    <div className="col-span-full flex justify-end">
      <Link to={`/admin/configuracoes/campos-telas?tela=${encodeURIComponent(tela)}`} className={classesBotao('fantasma', 'sm')}>
        <Settings2 className="size-4" aria-hidden />
        {t('camposTela.configurar')}
      </Link>
    </div>
  );
}
